import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { When } from 'react-if';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Box, Card, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography, TableSortLabel } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import { toast } from 'react-toastify';

import Loading from '../../loading';
import { RootState } from '../../../store';
import { PaginationFactory, PaginatorFactory } from '../../../factory';
import { ALERT_COLUMNS } from '../../../builders/alerts-builders';
import { AlertService } from '../../../services/alert.service';
import { AppointmentService } from '../../../services/appointment.service';
import { AlertTypes } from '../../../types';
import { AppointmentTypes } from '../../../types/appointment.types';
import { AlertListFilters } from './alerts-filters';
import { AlertDetailsDrawer } from '../../details-drawers/alert-details-drawer';
import { AppointmentDrawer } from '../../details-drawers/apppointments-drawer';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import { Label } from '../../label';
import NoData from '../../no-data';
import {RiskReviewDetailsDrawer} from "../../details-drawers/risk-review-details-drawer";
import {TeamService} from "../../../services/team.service";
import {TagsService} from "../../../services/tags-service";
import {GroupService} from "../../../services/groups.service";
import { ISO_DATE_RE } from "../../../utils/dates";
import { getDispatchDisposition, hasDispatchDisposition, isMonitoringCenterCanceled } from "../../../utils/alert-disposition";
import { getAlertTypeLabel } from "../../../helpers/alertTypeLabels";
import { getInitiatedByDescription } from "../../../helpers/initiatedByDescription";
const RISK_REVIEW_ALERT_TYPE = 'RiskReview';
const ALERT_STATUSES = ['active','resolved','reviewed','pending-review','escalated','expired'];
const VALID_ALERT_TYPES = new Set(['SOS', 'CheckOnMe', 'FakeCall', 'RiskReview', 'Shift']);
const VALID_ALERT_STATUSES = new Set(ALERT_STATUSES);

const buildFilterFromUrl = (sp: URLSearchParams): AlertTypes.Filters => {
    const next: any = { initiatedBy: '' };
    const urlAlertType = sp.get('alertType');
    const urlStatus = sp.get('status');
    const urlFrom = sp.get('fromDate');
    const urlTo = sp.get('toDate');
    if (urlAlertType && VALID_ALERT_TYPES.has(urlAlertType)) next.alertType = urlAlertType;
    if (urlStatus && VALID_ALERT_STATUSES.has(urlStatus)) next.status = urlStatus;
    // Only seed dates when BOTH are present and well-formed. Backend treats
    // `fromDate && toDate` as the trigger to apply a range — partial values
    // would be silently dropped server-side anyway.
    if (urlFrom && urlTo && ISO_DATE_RE.test(urlFrom) && ISO_DATE_RE.test(urlTo)) {
        next.fromDate = urlFrom;
        next.toDate = urlTo;
    }
    return next as AlertTypes.Filters;
};

export const AlertList: React.FC = () => {
    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<AlertTypes.Model>({} as AlertTypes.Model);
    const { pagination: { total, items }, loading } = useSelector((state: RootState) => state.alert);
    const [searchParams] = useSearchParams();
    const [filter, setFilter] = useState<AlertTypes.Filters>(() => {
        return buildFilterFromUrl(searchParams) as AlertTypes.Filters;
    });
    const {
        items: allTeams,
    } = useSelector((state: RootState) => state.teams);

    const {
        items: allTags,
    } = useSelector((state: RootState) => state.tags);

    const {
        items: allGroups,
    } = useSelector((state: RootState) => state.groups);

    // Appointment drawer state (opened from calendar icon or alert drawer link)
    const [appointmentDrawerOpen, setAppointmentDrawerOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState<AppointmentTypes.Model | null>(null);
    const [loadingAppointment, setLoadingAppointment] = useState<string | null>(null); // stores alertId being loaded

    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
      page: 0,
      perPage: 25,
    });
    const [sort, setSort] = useState<PaginatorFactory<any>['sort']>({ initiatedAt: 'desc' });


    const onFilter = async (filters: AlertTypes.Filters) => {
        setFilter(filters);
        setPagination({...pagination,page:0})
        await AlertService.fetchMany({ ...pagination, page: 1, filter: { ...filters } });
    }
  
    const makeCsvDownload = async () => {
        const res: PaginationFactory<AlertTypes.Model> = await AlertService.fetchMany(
            { perPage: 100000, page: 1, filter: { ...filter } },
            undefined,
            true,
            false,
        ) as PaginationFactory<AlertTypes.Model>;

        // tagId → name lookup for resolving each user's tag IDs into
        // human-readable names.
        const tagNameById = new Map<string, string>();
        for (const t of allTags) {
            if (t._id) tagNameById.set(t._id, t.name);
        }

        // Resolve each alert's user tags up front so we can size the tag
        // columns to the widest row (downloadCsv derives headers from
        // items[0].keys, so all rows must share the same shape).
        const alertTagLists: string[][] = (res.items ?? []).map((alert) =>
            (alert?.user?.tags ?? [])
                .map((tid) => tagNameById.get(tid))
                .filter((n): n is string => !!n)
        );
        const maxTags = alertTagLists.reduce((m, list) => Math.max(m, list.length), 0);
        // Mirrors the bulk-upload convention (tagOne, tagTwo, ...) and the
        // POM Users CSV — keeps both exports consistent and re-importable.
        const TAG_WORDS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
        const tagColKey = (i: number) => `tag${TAG_WORDS[i] ?? i + 1}`;

        const csvArray = res.items?.map((alert, idx) => {
            const tagCols: Record<string, string> = {};
            for (let i = 0; i < maxTags; i++) {
                tagCols[tagColKey(i)] = alertTagLists[idx][i] ?? '';
            }
            // Resolve team name once per row — getTeamName scans allTeams,
            // so calling it twice doubled the work per CSV row.
            const teamNameResolved = getTeamName(alert?.user?.teamId);
            return {
            userName: `${alert?.user?.firstName ?? ''} ${alert?.user?.lastName ?? ''}`,
            employeeId: alert?.user?.employeeId ?? '',
            groupName: alert?.user?.group?.name ?? '',
            teamName: teamNameResolved === 'N/A' ? '' : teamNameResolved,
            userPhoneNumber: alert?.user?.phoneNumber ?? '',
            initiatedBy: alert?.initiatedBy === 'AppInitiated' ? 'APP' : alert?.initiatedBy?.toUpperCase() ?? '',
            alertType: alert?.alertType ?? '',
            initiatedAt: alert?.initiatedAt ? dayjs(alert?.initiatedAt).format('MM/DD/YYYY hh:mm A') : 'N/A',
            status: (alert?.status === 'resolved' || alert?.status === 'reviewed')
                ? (alert?.isFalseAlarm === true
                    ? 'Resolved Test / False Alarm'
                    : getDispatchDisposition(alert)
                        ?? (isMonitoringCenterCanceled(alert) ? 'Resolved / Canceled' : getAlertStatusText(alert?.status ?? '')))
                : getAlertStatusText(alert?.status ?? ''),
            resolvedAt: alert?.resolvedAt ? dayjs(alert.resolvedAt).format('MM/DD/YYYY hh:mm A') : '',
            // Tags trail at the end so adding a new column later doesn't
            // shift tagOne/tagTwo/... and break any external integration
            // parsing this CSV.
            ...tagCols,
            };
        });

        downloadCsv(csvArray, 'pom-alerts');
    }

    const getAlertStatusText = (status: string) => {
        switch (status) {
            case 'active':
                return 'Active';
            case 'pending-review':
                return 'Pending Review';
            case 'reviewed':
                return 'Reviewed';
            case 'resolved':
                return 'Resolved';
            case 'escalated':
                return 'Escalated';
            case 'expired':
                return 'Expired';
            default:
                return 'N/A';
        }
    }
    useEffect(() => {
        AlertService.fetchMany({
            ...pagination,
            page: pagination.page + 1,
            filter,
            sort
        });
    }, [pagination?.perPage, pagination?.page, sort]);

    // Re-sync filter from URL when searchParams CHANGE (not on first mount).
    // Background: the lazy `useState` init only runs the first time the
    // component mounts — when the user deep-links into /alerts a second time
    // (e.g. dashboard "View in Alerts" while already on /alerts), React Router
    // re-uses the same component instance and the lazy init never re-fires.
    // Without this effect the URL params wouldn't actually take effect on
    // those subsequent visits.
    //
    // The didMount guard skips the first run so we don't double-fetch on the
    // initial mount: the pagination effect above already fetches with the
    // URL-seeded filter (lazy init reads URL synchronously). After mount, any
    // URL change runs setFilter + setPagination(page=0) + fetch.
    //
    // pagination.perPage / sort are read via closure capture from the most
    // recent render, so they reflect the latest user-chosen values at the
    // moment a URL change happens — not stale.
    const urlAlertType = searchParams.get('alertType');
    const urlStatus = searchParams.get('status');
    const urlFromDate = searchParams.get('fromDate');
    const urlToDate = searchParams.get('toDate');
    const didMountUrlSyncRef = useRef(false);
    useEffect(() => {
        if (!didMountUrlSyncRef.current) {
            didMountUrlSyncRef.current = true;
            return;
        }
        const next = buildFilterFromUrl(searchParams);
        setFilter(next);
        setPagination((p) => ({ ...p, page: 0 }));
        AlertService.fetchMany({ perPage: pagination.perPage, page: 1, filter: next, sort });
    // pagination.perPage / sort / searchParams intentionally omitted — see
    // comment above; the closure already captures latest values per-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlAlertType, urlStatus, urlFromDate, urlToDate]);

    useEffect(()=>{
        TeamService.fetchAll(()=>{})
        TagsService.fetchAll(() => {});
        GroupService.fetchAll(() => {});
    },[])

    const getTeamName = (teamId : any)=>{
        if(!teamId)
            return 'N/A'
        return allTeams?.find((t) => t?.id === teamId)?.name ?? 'N/A'

    }

    /**
     * Open the appointment drawer for a given appointmentId.
     * Closes the alert drawer first, fetches the enriched appointment, then opens the drawer.
     */
    const handleOpenAppointment = async (appointmentId: string) => {
        // Close alert drawer
        setDetailsModal(false);
        setSelectedAlert({} as AlertTypes.Model);

        setLoadingAppointment(appointmentId);
        try {
            const appt = await AppointmentService.fetchById(appointmentId);
            if (appt) {
                setSelectedAppointment(appt);
                setAppointmentDrawerOpen(true);
            } else {
                toast('Appointment not found.', { autoClose: 2000, type: 'error' });
            }
        } catch {
            toast('Failed to load appointment.', { autoClose: 2000, type: 'error' });
        } finally {
            setLoadingAppointment(null);
        }
    };

    return (
        <>
            <Card sx={{ mt: 2 }}>
                <AlertListFilters 
                    onFilter={onFilter}
                    onDownload={async () => {
                        await makeCsvDownload();
                    }}
                    allTeams={allTeams}
                    allTags={allTags}
                    allGroups={allGroups}
                />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow> 
                                {ALERT_COLUMNS?.map((column) => (
                                    <TableCell key={column.headerName}>
                                        {/* Columns explicitly opt out of sorting by setting
                                            sortable:false (e.g. derived/joined fields like Group
                                            that aren't server-sortable on the alert document). */}
                                        {(column as any).sortable !== false ? (
                                            <TableSortLabel
                                                active={!!sort![column.field]}
                                                direction={sort![column.field]}
                                                onClick={() => {
                                                    setSort((s) => ({
                                                        [column.field]: s![column.field] === 'asc' ? 'desc' : 'asc',
                                                    }));
                                                }}
                                            >
                                                <Typography  variant="body2" sx={{ color: 'text.primary' }}>
                                                    {column?.headerName}
                                                </Typography>
                                            </TableSortLabel>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                {column?.headerName}
                                            </Typography>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading?.fetchMany}>
                            <TableBody>
                                {items?.map((alert) => (
                                    <TableRow 
                                        key={alert?.id}
                                        sx={{ 
                                            ":hover": { 
                                                backgroundColor: '#F0F0F0',
                                                cursor: 'pointer'
                                            }}
                                        }
                                        onClick={() => {
                                            setSelectedAlert(alert);
                                            setDetailsModal(true);
                                        }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {alert?.user?.firstName || alert?.user?.lastName
                                                        ? [alert?.user?.firstName, alert?.user?.lastName].filter(Boolean).join(' ')
                                                        : '[Deleted User]'}
                                                </Typography>
                                                {alert?.user?.isDeleted && (
                                                    <Tooltip title="This user has been deleted — details shown as archived">
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                px: 0.75,
                                                                height: 18,
                                                                borderRadius: '9px',
                                                                bgcolor: '#FEE2E2',
                                                                color: '#991B1B',
                                                                fontSize: 10,
                                                                fontWeight: 700,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '.3px',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            Deleted
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {alert?.user?.group?.name ?? 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {getTeamName(alert?.user?.teamId)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {alert?.user?.phoneNumber || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={getInitiatedByDescription(alert?.initiatedBy)} placement='left' arrow>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {alert?.initiatedBy === 'AppInitiated' ? 'APP' : alert?.initiatedBy?.toUpperCase() || '----'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {getAlertTypeLabel(alert?.alertType)}
                                                </Typography>
                                                {alert?.isTest === true && (
                                                    <Tooltip title="Triggered in Test Mode — routed to the training line, excluded from metrics">
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                px: 0.75,
                                                                height: 18,
                                                                borderRadius: '9px',
                                                                bgcolor: '#FEF3C7',
                                                                color: '#92400E',
                                                                fontSize: 10,
                                                                fontWeight: 700,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '.3px',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            Test
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                {alert?.isFalseAlarm === true && (
                                                    <Tooltip title="This alert was a test / false alarm" placement="top" arrow>
                                                        <InfoOutlinedIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color: 'warning.main',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {hasDispatchDisposition(alert) && (
                                                    <Tooltip title="Emergency services were dispatched for this alert" placement="top" arrow>
                                                        <ErrorIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color: 'error.main',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {alert?.appointmentTitle && alert?.appointmentId && (
                                                    <Tooltip title={`Appointment: ${alert.appointmentTitle}`} placement="top" arrow>
                                                        {loadingAppointment === alert.appointmentId ? (
                                                            <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                                                        ) : (
                                                            <CalendarTodayIcon
                                                                sx={{
                                                                    fontSize: 16,
                                                                    color: 'primary.main',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { opacity: 0.7 },
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenAppointment(alert.appointmentId!);
                                                                }}
                                                            />
                                                        )}
                                                    </Tooltip>
                                                )}
                                                {alert?.appointmentTitle && !alert?.appointmentId && (
                                                    <Tooltip title={`Appointment: ${alert.appointmentTitle}`} placement="top" arrow>
                                                        <CalendarTodayIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color: 'primary.main',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {dayjs(alert?.initiatedAt).format('MM/DD/YYYY hh:mm A')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <When condition={alert?.status === 'active' || alert?.status === 'pending-review'}>
                                                <Label
                                                    variant={'ghost'}
                                                    color={'error'}
                                                >
                                                    {getAlertStatusText(alert?.status)}
                                                </Label>
                                            </When>
                                            <When condition={alert?.status === 'resolved' || alert?.status === 'reviewed'}>
                                                {alert?.isFalseAlarm === true ? (
                                                    <Tooltip title="This alert was a test / false alarm" placement="top" arrow>
                                                        <span>
                                                            <Label
                                                                variant={'ghost'}
                                                                color={'warning'}
                                                            >
                                                                Resolved Test / False Alarm
                                                            </Label>
                                                        </span>
                                                    </Tooltip>
                                                ) : getDispatchDisposition(alert) ? (
                                                    <Tooltip title="Emergency services were dispatched for this alert" placement="top" arrow>
                                                        <span>
                                                            <Label
                                                                variant={'ghost'}
                                                                color={'error'}
                                                            >
                                                                {getDispatchDisposition(alert)}
                                                            </Label>
                                                        </span>
                                                    </Tooltip>
                                                ) : isMonitoringCenterCanceled(alert) ? (
                                                    <Tooltip title="The monitoring center canceled this alarm" placement="top" arrow>
                                                        <span>
                                                            <Label
                                                                variant={'ghost'}
                                                                color={'warning'}
                                                            >
                                                                Resolved / Canceled
                                                            </Label>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Label
                                                        variant={'ghost'}
                                                        color={'success'}
                                                    >
                                                        {getAlertStatusText(alert?.status)}
                                                    </Label>
                                                )}
                                            </When>
                                            <When condition={alert?.status === 'escalated' || alert?.status === 'expired'}>
                                                <Label
                                                    variant={'ghost'}
                                                    color={'warning'}
                                                >
                                                    {getAlertStatusText(alert?.status)}
                                                </Label>
                                            </When>
                                            <When condition={!(ALERT_STATUSES.includes(alert?.status))}>
                                                <Label
                                                    variant={'ghost'}
                                                    color={'info'}
                                                >
                                                    {getAlertStatusText(alert?.status)}
                                                </Label>
                                            </When>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>
                    <When
                        condition={
                            loading?.fetchMany === false && (!items || items?.length < 1)
                        }
                    >
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No alerts found'}
                            description={
                                'There are no alerts created.'
                            }
                        />
                    </When>
                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) => setPagination(pag => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) => setPagination(pag => ({ ...pag, perPage: parseInt(e.target.value, 10) }))}
                />
            </Card>

            {detailsModal && selectedAlert && selectedAlert.alertType !== RISK_REVIEW_ALERT_TYPE &&
                <AlertDetailsDrawer
                    type={selectedAlert?.alertType}
                    alert={selectedAlert}
                    visible={detailsModal}
                    onClose={() => {
                        setDetailsModal(false);
                        setSelectedAlert({} as AlertTypes.Model);
                    }}
                    onOpenAppointment={(appointmentId) => {
                        handleOpenAppointment(appointmentId);
                    }}
                />
            }
            {detailsModal && selectedAlert && selectedAlert.alertType === RISK_REVIEW_ALERT_TYPE &&
                <RiskReviewDetailsDrawer
                    type={selectedAlert?.alertType}
                    alert={selectedAlert}
                    visible={detailsModal}
                    onClose={() => {
                        setDetailsModal(false);
                        setSelectedAlert({} as AlertTypes.Model);
                    }}
                />
            }

            {/* Appointment details drawer (opened from calendar icon or alert drawer link) */}
            {appointmentDrawerOpen && selectedAppointment && (
                <AppointmentDrawer
                    visible={appointmentDrawerOpen}
                    selectedAppointment={selectedAppointment}
                    onClose={() => {
                        setAppointmentDrawerOpen(false);
                        setSelectedAppointment(null);
                    }}
                />
            )}
        </>
    )
}